/**
 * Build-time configuration from `VITE_*` environment variables (see `.env.example`).
 * Everything here ends up in the client bundle — never put secrets in it.
 */
export interface AppConfig {
  readonly unitId: string;
  readonly hospitalName: string;
  /** Shows the demo PIN hint and the simulate-breach control. */
  readonly demoMode: boolean;
  readonly demoPin: string;
  readonly demoOperatorName: string;
  /** Compresses the countdown ×4 and the cycle ×20. */
  readonly timeAccelerated: boolean;
  readonly soundEnabled: boolean;
  /** When false, EMERGENCY STOP aborts on the first tap with no confirmation dialog. */
  readonly estopRequiresConfirmation: boolean;
  /**
   * Base URL of the control API: "" for same origin, or an absolute origin. Null runs fully
   * offline against simulated services (design review and demos).
   */
  readonly apiUrl: string | null;
  /**
   * How the lamp is driven. cloud: through the control API and Shelly Cloud (needs data). direct:
   * the tablet drives the robot's switch over Wi-Fi, signs in and records cycles with no data, and
   * syncs when it can (Android app only; needs VITE_API_URL).
   */
  readonly control: 'cloud' | 'direct';
  /** Sent when the tablet enrols, for the server's records. */
  readonly appVersion: string;
  /**
   * The website the app is published on (its install page tells the tablet about updates):
   * VITE_SITE_URL, or the API's origin when that is absolute. Null for the web app itself.
   */
  readonly siteUrl: string | null;
  /** Longest manual "lamp on" offered in the lamp panel; the API enforces its own limit too. */
  readonly manualLampMaxSeconds: number;
}

type Env = Readonly<Record<string, string | boolean | undefined>>;

function text(env: Env, key: string, fallback: string): string {
  const value = env[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback;
}

function flag(env: Env, key: string, fallback: boolean): boolean {
  const value = env[key];
  if (typeof value === 'boolean') return value;
  if (value === undefined || value.trim() === '') return fallback;
  return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
}

export function loadConfig(env: Env): AppConfig {
  return {
    unitId: text(env, 'VITE_UNIT_ID', 'EVE20049'),
    hospitalName: text(env, 'VITE_HOSPITAL_NAME', 'Milpark'),
    demoMode: flag(env, 'VITE_DEMO_MODE', true),
    demoPin: text(env, 'VITE_DEMO_PIN', '1234'),
    demoOperatorName: text(env, 'VITE_DEMO_OPERATOR_NAME', 'Tendani Mamburu'),
    // Real time unless a demo build asks otherwise: a compressed cycle must never reach a real lamp.
    timeAccelerated: flag(env, 'VITE_TIME_ACCELERATED', false),
    soundEnabled: flag(env, 'VITE_SOUND', true),
    estopRequiresConfirmation: flag(env, 'VITE_ESTOP_CONFIRM', true),
    apiUrl: apiUrl(env),
    control: text(env, 'VITE_CONTROL', 'cloud') === 'direct' ? 'direct' : 'cloud',
    appVersion: text(env, 'VITE_APP_VERSION', '0.2.0'),
    siteUrl: siteUrl(env),
    manualLampMaxSeconds: Math.max(
      5,
      Number(text(env, 'VITE_MANUAL_LAMP_MAX_SECONDS', '60')) || 60,
    ),
  };
}

function siteUrl(env: Env): string | null {
  const explicit = text(env, 'VITE_SITE_URL', '');
  if (explicit) return explicit.replace(/\/+$/, '');
  const api = apiUrl(env);
  return api && /^https?:\/\//.test(api) ? api : null;
}

/** VITE_API_URL: unset → offline demo; "/" → same origin; otherwise an absolute origin. */
function apiUrl(env: Env): string | null {
  const value = env.VITE_API_URL;
  if (typeof value !== 'string' || value.trim() === '') return null;
  return value.trim().replace(/\/+$/, '');
}
